import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { FloatingPortal } from "@floating-ui/react";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faExpand } from "@fortawesome/pro-regular-svg-icons";
import { AnimatePresence } from "motion/react";
import { twJoin, twMerge } from "tailwind-merge";

import {
  FloatingWindowInternal,
  getMaximizedWindowGeometry,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { useCaseBlockScrollTarget } from "src/cases/caseView/CaseBlockScrollContext";
import { Icon, IconButton } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import { Tooltip } from "src/design-system/Tooltip";

const ExpandedBlockContext = createContext(false);

export const useIsBlockExpanded = () => useContext(ExpandedBlockContext);

export type CaseBlockPosition = {
  column: number;
  row: number;
  width: number;
  height: number;
};

export type CaseBlockProps = {
  title: string;
  icon?: IconDefinition | null;
  rightSlot?: ReactNode;
  leftSlot?: ReactNode;
  children: ReactNode;
  position: CaseBlockPosition;
  noPaddings?: boolean;
  isLoading?: boolean;
  dataLoc?: string;
  blockKey: string;
  noScroll?: boolean;
  isExpandable?: boolean;
  expandedRightSlot?: ReactNode;
};

export const CaseBlock: React.FC<CaseBlockProps> = ({
  title,
  icon,
  rightSlot,
  leftSlot,
  children,
  position,
  noPaddings = false,
  isLoading = false,
  dataLoc = "case-block",
  blockKey,
  noScroll = false,
  isExpandable = false,
  expandedRightSlot,
}) => {
  const [isBlinking, triggerBlink] = useCaseBlockBlink();
  // Offset and size the expanded window opens at, measured once when the block
  // is expanded: the window is draggable and resizable from there, so it must
  // not follow later viewport changes.
  const [expandedGeometry, setExpandedGeometry] = useState<
    { x: number; y: number; width: number; height: number } | undefined
  >(undefined);

  const { ref: blockRef } = useCaseBlockScrollTarget<HTMLDivElement>(blockKey, {
    onScrollEnd: triggerBlink,
  });

  const gridPosition = {
    gridColumn: `${position.column} / span ${position.width}`,
    gridRow: `${position.row} / span ${position.height}`,
  };

  const expandButton = isExpandable && (
    <Tooltip delay={500} placement="top" title="Expand" asChild>
      <IconButton
        dataLoc="case-block-expand"
        icon={faExpand}
        size="xs"
        onClick={() => setExpandedGeometry(getMaximizedWindowGeometry())}
      />
    </Tooltip>
  );

  const headerActions = (expandButton || rightSlot) && (
    <div className="flex items-center self-stretch">
      {rightSlot}
      {expandButton && rightSlot && (
        <div className="-my-4 mr-4 ml-3 w-px self-stretch bg-gray-100" />
      )}
      {expandButton}
    </div>
  );

  const contentClassName = twJoin(
    "flex flex-col",
    noScroll ? "overflow-hidden" : "decideScrollbar overflow-auto",
    !noPaddings && "p-4",
  );

  const content = isLoading ? (
    <div className={twJoin("flex flex-col gap-4", noPaddings && "p-4")}>
      <Skeleton height="h-6" width="w-full" />
      <Skeleton height="h-6" width="w-full" />
      <Skeleton height="h-6" width="w-full" />
    </div>
  ) : (
    children
  );

  return (
    <div
      ref={blockRef}
      className={twMerge(
        "flex scroll-mt-3 flex-col overflow-clip rounded-lg border border-gray-200 bg-white transition-all duration-300",
        isBlinking && "border-indigo-300 shadow-gray-200 shadow-lg",
      )}
      data-loc={isLoading ? `${dataLoc}-loading` : dataLoc}
      style={gridPosition}
    >
      <div
        className="flex items-center gap-2 border-gray-200 border-b p-4"
        data-loc={dataLoc ? `${dataLoc}-header` : "case-block-header"}
      >
        {icon && (
          <Icon
            variant="hover"
            dataLoc={dataLoc ? `${dataLoc}-icon` : "case-block-icon"}
            icon={icon}
            size="2xs"
          />
        )}
        <h3 className="flex-1 truncate font-inter-semibold-13px text-gray-900">
          {title}
        </h3>
        {leftSlot && (
          <div
            className="flex items-center"
            data-loc={dataLoc ? `${dataLoc}-left-slot` : "case-block-left-slot"}
          >
            {leftSlot}
          </div>
        )}
        {headerActions && (
          <div
            className="flex items-center"
            data-loc={
              dataLoc ? `${dataLoc}-right-slot` : "case-block-right-slot"
            }
          >
            {headerActions}
          </div>
        )}
      </div>
      <div
        className={twMerge(contentClassName, "flex-1")}
        data-loc={dataLoc ? `${dataLoc}-content` : "case-block-content"}
      >
        {content}
      </div>
      <AnimatePresence initial={false}>
        {expandedGeometry && (
          <FloatingPortal>
            <FloatingWindowInternal
              dataLoc="case-block-expanded"
              height={expandedGeometry.height}
              isPinned={false}
              minHeight="h-96"
              minWidth="w-120"
              style={{
                inset: "0px auto auto 0px",
                transform: `translate3d(${expandedGeometry.x}px, ${expandedGeometry.y}px, 0)`,
              }}
              title={
                <span className="truncate font-inter-semibold-14px text-gray-900">
                  {title}
                </span>
              }
              titleRight={
                <div className="flex h-9 items-center">{expandedRightSlot}</div>
              }
              width={expandedGeometry.width}
              draggable
              fullBodyWidth
              resizeable
              onClose={() => setExpandedGeometry(undefined)}
            >
              <ExpandedBlockContext value={true}>
                <div
                  className={twMerge(contentClassName, "h-full")}
                  data-loc={
                    dataLoc
                      ? `${dataLoc}-expanded-content`
                      : "case-block-expanded-content"
                  }
                >
                  {content}
                </div>
              </ExpandedBlockContext>
            </FloatingWindowInternal>
          </FloatingPortal>
        )}
      </AnimatePresence>
    </div>
  );
};

const useCaseBlockBlink = () => {
  const [isBlinking, setIsBlinking] = useState<boolean>(false);
  const timeoutRef = useRef<number | null>(null);

  const triggerBlink = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
    }

    setIsBlinking(true);

    timeoutRef.current = window.setTimeout(() => {
      setIsBlinking(false);
      timeoutRef.current = null;
    }, 600);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return [isBlinking, triggerBlink] as const;
};
